// Google Analytics Service
// Serviço para integração com Google Analytics GA4

class AnalyticsService {
  constructor() {
    this.GA_MEASUREMENT_ID = 'G-BLX997QCM1';
    this.isInitialized = false;
    this.init();
  }

  // Inicializar o serviço
  init() {
    if (typeof window !== 'undefined' && window.gtag) {
      this.isInitialized = true;
    } else {
      console.warn('Google Analytics não está disponível');
    }
  }

  // Verificar se o GA está disponível
  isAvailable() {
    return this.isInitialized && typeof window !== 'undefined' && window.gtag;
  }

  // Rastrear visualização de página
  trackPageView(pagePath, pageTitle) {
    if (!this.isAvailable()) return;

    window.gtag('config', this.GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }

  // Rastrear evento personalizado
  trackEvent(eventName, parameters = {}) {
    if (!this.isAvailable()) return;

    window.gtag('event', eventName, {
      event_category: parameters.category || 'engagement',
      event_label: parameters.label || '',
      value: parameters.value || 0,
      ...parameters
    });
  }

  // Rastrear login
  trackLogin(method = 'email') {
    this.trackEvent('login', {
      method: method,
      event_category: 'authentication'
    });
  }

  // Rastrear logout
  trackLogout() {
    this.trackEvent('logout', {
      event_category: 'authentication'
    });
  }

  // Rastrear clique em botão
  trackButtonClick(buttonName, section = '') {
    this.trackEvent('click', {
      event_category: 'button',
      event_label: buttonName,
      section: section
    });
  }

  // Rastrear navegação no menu
  trackNavigation(destination) {
    this.trackEvent('navigate', {
      event_category: 'navigation',
      event_label: destination
    });
  }

  // Rastrear interação com WhatsApp
  trackWhatsAppInteraction(action, instanceName = '') {
    this.trackEvent('whatsapp_interaction', {
      event_category: 'whatsapp',
      action: action,
      instance_name: instanceName
    });
  }

  // Rastrear uso do CRM
  trackCRMAction(action, contactId = '') {
    this.trackEvent('crm_action', {
      event_category: 'crm',
      action: action,
      contact_id: contactId
    });
  }

  // Rastrear visualização de dashboard
  trackDashboardView(dashboardType) {
    this.trackEvent('dashboard_view', {
      event_category: 'dashboard',
      dashboard_type: dashboardType
    });
  }

  // Rastrear erro
  trackError(errorType, errorMessage = '') {
    this.trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      error_type: errorType
    });
  }

  // Rastrear tempo de sessão
  trackSessionDuration(duration) {
    this.trackEvent('session_duration', {
      event_category: 'engagement',
      value: Math.round(duration / 1000), // em segundos
      custom_parameter_duration: duration
    });
  }

  // Rastrear conversão/meta
  trackConversion(conversionName, value = 0) {
    this.trackEvent('conversion', {
      event_category: 'conversion',
      event_label: conversionName,
      value: value
    });
  }

  // Rastrear download de arquivo
  trackDownload(fileName, fileType = '') {
    this.trackEvent('file_download', {
      event_category: 'download',
      event_label: fileName,
      file_type: fileType
    });
  }

  // Rastrear busca
  trackSearch(searchTerm, resultsCount = 0) {
    this.trackEvent('search', {
      search_term: searchTerm,
      event_category: 'search',
      results_count: resultsCount
    });
  }

  // Definir propriedades do usuário
  setUserProperties(properties) {
    if (!this.isAvailable()) return;

    window.gtag('config', this.GA_MEASUREMENT_ID, {
      custom_map: properties
    });
  }

  // Definir ID do usuário
  setUserId(userId) {
    if (!this.isAvailable()) return;

    window.gtag('config', this.GA_MEASUREMENT_ID, {
      user_id: userId
    });
  }

  // Rastrear timing personalizado
  trackTiming(category, variable, value, label = '') {
    this.trackEvent('timing_complete', {
      name: variable,
      value: value,
      event_category: category,
      event_label: label
    });
  }
}

// Criar instância única do serviço
const analyticsService = new AnalyticsService();

export default analyticsService;

// Exportar também métodos individuais para facilitar o uso
export const {
  trackPageView,
  trackEvent,
  trackLogin,
  trackLogout,
  trackButtonClick,
  trackNavigation,
  trackWhatsAppInteraction,
  trackCRMAction,
  trackDashboardView,
  trackError,
  trackSessionDuration,
  trackConversion,
  trackDownload,
  trackSearch,
  setUserProperties,
  setUserId,
  trackTiming
} = analyticsService;